import { useMemo, useState } from "react";
import { query } from "../api/client";
import { useQuery } from "../api/useQuery";
import { useAuth } from "../auth/AuthContext";
import { LazyMap } from "../components/LazyMap";
import { fmtDateTime, priorityName } from "../components/format";
import type { ActivityCell, MapMarker } from "../components/MapView";
import type { Incident, MapLayers, Observation, Page, User } from "../api/types";

const PERIODS: [string, string, number][] = [
  ["1", "Сутки", 1],
  ["7", "7 дней", 7],
  ["30", "30 дней", 30],
  ["90", "Квартал", 90],
];

type Layer = "tags" | "tracks" | "inspectors" | "incidents" | "observations" | "activity";

const LAYERS: [Layer, string][] = [
  ["tags", "Контрольные точки"],
  ["inspectors", "Инспекторы"],
  ["tracks", "Треки"],
  ["incidents", "Инциденты"],
  ["observations", "Наблюдения"],
  ["activity", "Активность"],
];

interface Position {
  inspector_name: string;
  territory_name: string;
  lat: number;
  lon: number;
  accuracy_m: number;
  at: string;
  patrol_active: boolean;
}

/** «12 мин назад» — время последней точки, без притворства «онлайн». */
function ago(iso: string, now: number): string {
  const min = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h} ч ${min % 60} мин назад` : fmtDateTime(iso);
}

/** Сколько минут без новых точек считается «давно»: дальше — серый маркер. */
const STALE_MIN = 15;

export function MapPage() {
  const { me } = useAuth();
  const { data, error, loading } = useQuery<MapLayers>("/api/v1/map/layers");
  const [on, setOn] = useState<Record<Layer, boolean>>({
    tags: true, inspectors: true, tracks: false, incidents: true, observations: false, activity: false,
  });
  const [period, setPeriod] = useState("1");
  const [inspector, setInspector] = useState("");
  const [route, setRoute] = useState("");
  const users = useQuery<{ items: User[] }>(me?.can_manage_users ? "/api/v1/users" : null);
  const canSeePositions = me?.role !== "inspector";

  // Начало периода фиксируется при выборе, а не при каждой отрисовке.
  const from = useMemo(() => {
    const days = PERIODS.find(([id]) => id === period)?.[2] ?? 1;
    return new Date(Date.now() - days * 86400000).toISOString();
  }, [period]);

  const tracks = useQuery<{ items: { geometry: unknown }[]; truncated: boolean }>(
    on.tracks ? "/api/v1/map/tracks" + query({ from, inspector_id: inspector, route_id: route }) : null);
  const positions = useQuery<{ items: Position[]; server_time: string }>(
    on.inspectors && canSeePositions ? "/api/v1/map/inspectors" : null);
  const incidents = useQuery<Page<Incident>>(on.incidents ? "/api/v1/incidents" + query({ from, limit: 200 }) : null);
  const observations = useQuery<Page<Observation>>(
    on.observations ? "/api/v1/observations" + query({ from, limit: 200 }) : null);
  const activity = useQuery<{ cells: ActivityCell[] }>(on.activity ? "/api/v1/map/activity" + query({ from }) : null);

  const markers = useMemo<MapMarker[]>(() => {
    const out: MapMarker[] = [];
    if (on.tags) {
      for (const t of data?.nfc_tags ?? []) {
        out.push({ lat: t.lat, lon: t.lon, label: `${t.point_name} · радиус ${t.radius_m} м`, tone: "neutral" });
      }
    }
    if (on.incidents) {
      for (const i of incidents.data?.items ?? []) {
        if (i.lat === null || i.lon === null) continue;
        out.push({ lat: i.lat, lon: i.lon, tone: i.priority <= 2 ? "danger" : "warn",
          label: `${i.category_name} · ${priorityName(i.priority)} · ${fmtDateTime(i.device_time)}` });
      }
    }
    if (on.observations) {
      for (const o of observations.data?.items ?? []) {
        if (o.lat === null || o.lon === null) continue;
        out.push({ lat: o.lat, lon: o.lon, tone: "ok",
          label: `${o.verified_species_name ?? o.species_name} · ${fmtDateTime(o.device_time)}` +
            (o.location_masked ? " · место загрублено (охраняемый вид)" : "") });
      }
    }
    if (on.inspectors && positions.data) {
      const now = new Date(positions.data.server_time).getTime();
      for (const p of positions.data.items) {
        const stale = now - new Date(p.at).getTime() > STALE_MIN * 60000;
        out.push({ lat: p.lat, lon: p.lon, tone: !p.patrol_active ? "neutral" : stale ? "warn" : "ok",
          label: `${p.inspector_name} · последняя точка ${ago(p.at, now)} · ±${Math.round(p.accuracy_m)} м` +
            (p.patrol_active ? "" : " · обход завершён") });
      }
    }
    return out;
  }, [data, on, incidents.data, observations.data, positions.data]);

  const polygons = useMemo(() => (data?.territories ?? []).map((t) => t.boundary).filter(Boolean), [data]);
  const routes = useMemo(() => (data?.routes ?? []).map((r) => r.path).filter(Boolean), [data]);
  const trackLines = useMemo(() => (tracks.data?.items ?? []).map((t) => t.geometry), [tracks.data]);
  const errors = [error, tracks.error, positions.error, incidents.error, observations.error, activity.error].filter(Boolean);

  return (
    <>
      <div className="page-head">
        <h2>Карта</h2>
        {data && (
          <p className="muted small" style={{ margin: 0 }}>
            территорий: {data.territories.length} · маршрутов: {data.routes.length} ·
            контрольных точек: {data.nfc_tags.length}
          </p>
        )}
      </div>

      <div className="filters" style={{ alignItems: "center" }}>
        <div className="segmented" role="group" aria-label="Слои">
          {LAYERS.filter(([id]) => id !== "inspectors" || canSeePositions).map(([id, title]) => (
            <button key={id} aria-pressed={on[id]} onClick={() => setOn({ ...on, [id]: !on[id] })}>{title}</button>
          ))}
        </div>
      </div>
      <div className="filters">
        <label className="field">Период
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIODS.map(([id, title]) => <option key={id} value={id}>{title}</option>)}
          </select>
        </label>
        {on.tracks && users.data && (
          <label className="field">Инспектор
            <select value={inspector} onChange={(e) => setInspector(e.target.value)}>
              <option value="">Все</option>
              {users.data.items.filter((u) => u.role === "inspector").map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </label>
        )}
        {on.tracks && (
          <label className="field">Маршрут
            <select value={route} onChange={(e) => setRoute(e.target.value)}>
              <option value="">Все</option>
              {data?.routes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
        )}
      </div>

      {errors.map((e, i) => <div key={i} className="error" role="alert">{e}</div>)}
      {loading && <p className="notice">Загрузка слоёв…</p>}

      <p className="muted small">
        {on.inspectors && canSeePositions && "Инспекторы — по последней точке, дошедшей до сервера; это не живое отслеживание: без связи точка устаревает. "}
        {on.tracks && tracks.data?.truncated && "Показаны последние 300 обходов — сузьте период или фильтры. "}
        {on.activity && "Активность — число обходов по ячейкам около 250 м. "}
      </p>

      {data && polygons.length + routes.length + markers.length + trackLines.length === 0 && !on.activity && (
        <p className="muted small">За выбранный период на карте нечего показать.</p>
      )}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <LazyMap polygons={polygons} routes={routes} markers={markers} tracks={trackLines}
          cells={on.activity ? activity.data?.cells ?? [] : []} height={600} fitKey="map" />
      </div>
    </>
  );
}
